"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import LanguageDropdown from "./LanguageDropdown";

const CountryDropdown = ({ isHeroSection }) => {
  const dropdownRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Countries config
const countries = [
  {
    code: "global",
    name: "Global",
    flag: "/images/flags/globe.png",
    enabled: true,
    languages: ["en"],
  },
  {
    code: "gy",
    name: "Guyana",
    flag: "/images/flags/guyana.png",
    enabled: true,
    languages: ["en", "es"], // English & Spanish
  },
  {
    code: "iq",
    name: "Iraq",
    flag: "/images/flags/iraq.png",
    enabled: true,
    languages: ["ar-IQ", "ckb", "en", "sdh"], // Arabic, Kurdish, English
  },
  {
    code: "jo",
    name: "Jordan",
    flag: "/images/flags/jordan.png",
    enabled: false,
    languages: ["ar-IQ", "en"],
  },
  {
    code: "ly",
    name: "Libya",
    flag: "/images/flags/libya.png",
    enabled: false,
    languages: ["ar", "en"],
  },
];

  // Detect country from subdomain
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hostname = window.location.hostname;

    let subdomain = null;
    let rootDomain;

    if (hostname.includes("localhost")) {
      const parts = hostname.split(".");
      if (parts.length > 1) {
        subdomain = parts[0];
      }
      rootDomain = "localhost";
    } else {
      const parts = hostname.split(".");
      if (parts.length > 2) {
        subdomain = parts[0];
        rootDomain = parts.slice(-2).join(".");
      } else {
        rootDomain = hostname;
      }
    }

    const matchedCountry = countries.find(
      (country) => country.code === subdomain && country.enabled,
    );

    // ✅ If no country subdomain → show Global
    if (matchedCountry) {
      setSelectedCountry(matchedCountry);
    } else {
      setSelectedCountry(countries.find((c) => c.code === "global"));
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Redirect to selected country subdomain
  const selectCountry = (country) => {
    if (!country.enabled) return;

    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    let rootDomain;

    if (hostname.includes("localhost")) {
      rootDomain = "localhost:3000";
    } else {
      const parts = hostname.split(".");
      rootDomain = parts.slice(-2).join(".");
    }

    let newUrl;

    // ✅ Global → root domain
    if (country.code === "global") {
      newUrl = `${protocol}//${rootDomain}`;
    } else {
      newUrl = `${protocol}//${country.code}.${rootDomain}`;
    }

    window.location.replace(newUrl);
  };

  // Chevron icon
  const ChevronSVG = () => (
    <svg
      width="14"
      height="8"
      viewBox="0 0 14 8"
      fill="none"
      style={{
        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.3s ease",
      }}
    >
      <path
        d="M1 1L7 7L13 1"
        stroke={isHeroSection ? "#fff" : "#000"}
        strokeWidth="2"
      />
    </svg>
  );

  return (
    <div style={{display: "flex", gap: 5, alignItems: "center"}}>
        <div
      className={`country_dropdown_wrapper ${!isHeroSection ? "dark" : ""}`}
      ref={dropdownRef}
    >
      {/* BUTTON */}
      <button className="country_dropdown_button" onClick={toggleDropdown}>
        <div className="selected_country">
          <Image
            src={selectedCountry?.flag || "/images/flags/globe.png"}
            width={24}
            height={24}
            alt="flag"
          />

          <span>{selectedCountry?.name || "Select Country"}</span>
        </div>

        <ChevronSVG />
      </button>

      {/* MENU */}
      {isOpen && (
        <div className="country_dropdown_menu">
          {countries
            .filter((c) => c.code !== selectedCountry?.code)
            .map((country) => (
              <div
                key={country.code}
                className={`country_dropdown_item ${
                  !country.enabled ? "disabled" : ""
                }`}
                onClick={() => selectCountry(country)}
              >
                <Image
                  src={country.flag}
                  width={24}
                  height={24}
                  alt={"country"}
                />

                <span>{country.name}</span>
              </div>
            ))}
        </div>
      )}
    </div>
    <LanguageDropdown selectedCountry={selectedCountry} />
    </div>
  );
};

export default CountryDropdown;
